<template>
  <v-dialog
    v-model="dialog"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <v-card class="d-flex flex-column fill-height" style="background-color: #2b2b2b">
      <!-- Toolbar -->
      <v-toolbar dark color="primary" dense flat style="flex: 0 0 auto">
        <v-btn icon dark title="Takaisin" @click="close">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="text-truncate text-subtitle-1 text-sm-h6">
          {{ title || "Pelastuskortti" }}
        </v-toolbar-title>
        <v-spacer />

        <!-- Zoom controls -->
        <template v-if="!loading && !loadError && numPages > 0">
          <v-btn
            icon
            dark
            title="Loitonna"
            :disabled="zoom <= 0.6"
            @click="changeZoom(-0.25)"
          >
            <v-icon small>mdi-magnify-minus-outline</v-icon>
          </v-btn>
          <span
            class="text-caption mx-1"
            style="min-width: 40px; text-align: center;"
          >
            {{ Math.round(zoom * 100) }}%
          </span>
          <v-btn
            icon
            dark
            title="Lähennä"
            :disabled="zoom >= 3.0"
            @click="changeZoom(0.25)"
          >
            <v-icon small>mdi-magnify-plus-outline</v-icon>
          </v-btn>
          <v-btn icon dark title="Sovita leveys" @click="resetZoom">
            <v-icon small>mdi-fit-to-page-outline</v-icon>
          </v-btn>
        </template>

        <v-btn
          v-if="url"
          icon
          dark
          :href="url"
          target="_blank"
          rel="noopener noreferrer"
          title="Avaa laitteen katseluohjelmassa / erillisessä selaimessa"
        >
          <v-icon>mdi-open-in-new</v-icon>
        </v-btn>
        <v-btn text dark class="px-2" @click="close">
          Sulje
        </v-btn>
      </v-toolbar>

      <!-- Content Area -->
      <div
        ref="canvasContainer"
        class="flex-grow-1 overflow-y-auto pa-2 text-center"
        style="position: relative; width: 100%; height: calc(100vh - 48px); background-color: #3f3f3f; -webkit-overflow-scrolling: touch;"
      >
        <!-- Loading state -->
        <div
          v-if="loading"
          class="d-flex flex-column align-center justify-center fill-height white--text"
          style="min-height: 300px"
        >
          <v-progress-circular indeterminate color="primary" size="56" width="5" />
          <div class="mt-4 text-subtitle-2">
            Ladataan pelastuskorttia...
          </div>
        </div>

        <!-- Error state -->
        <div
          v-else-if="loadError"
          class="d-flex flex-column align-center justify-center fill-height white--text pa-4"
          style="min-height: 300px"
        >
          <v-icon color="warning" size="48">mdi-alert-circle-outline</v-icon>
          <div class="mt-3 text-subtitle-1">Pelastuskortin lataaminen epäonnistui</div>
          <div class="text-caption grey--text text--lighten-1 mt-1">
            {{ loadError }}
          </div>
          <div class="mt-4">
            <v-btn color="primary" class="mr-2" @click="loadPdf">
              <v-icon left>mdi-refresh</v-icon>
              Yritä uudelleen
            </v-btn>
            <v-btn
              v-if="url"
              outlined
              dark
              :href="url"
              target="_blank"
              rel="noopener noreferrer"
            >
              <v-icon left>mdi-open-in-new</v-icon>
              Avaa suoraan selaimessa
            </v-btn>
          </div>
        </div>

        <!-- Rendered Canvas Pages -->
        <div v-else-if="numPages > 0" class="d-flex flex-column align-center my-2">
          <div
            v-for="p in numPages"
            :key="p"
            class="mb-3 elevation-4 white"
            style="line-height: 0; max-width: 100%; overflow: auto;"
          >
            <canvas :ref="`canvas_${p}`" style="display: block; margin: 0 auto;" />
            <div
              class="pa-1 text-caption grey darken-3 white--text text-right"
              style="line-height: 1;"
            >
              Sivu {{ p }} / {{ numPages }}
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
let pdfJsLoadingPromise = null;

function loadPdfJs() {
  if (typeof window === "undefined") return Promise.reject(new Error("No window"));
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfJsLoadingPromise) return pdfJsLoadingPromise;

  pdfJsLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "/pdfjs/pdf.min.js";
    script.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.js";
        resolve(window.pdfjsLib);
      } else {
        reject(new Error("pdfjsLib not found on window"));
      }
    };
    script.onerror = () => {
      pdfJsLoadingPromise = null;
      reject(new Error("Failed to load /pdfjs/pdf.min.js"));
    };
    document.head.appendChild(script);
  });

  return pdfJsLoadingPromise;
}

export default {
  name: "PelastuskorttiDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    url: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
  },
  data: () => ({
    loading: true,
    loadError: null,
    numPages: 0,
    zoom: 1.0,
    pushedHistory: false,
    pdfDoc: null,
    renderTasks: [],
  }),
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.setupHistory();
        this.loadPdf();
      } else {
        this.cleanupHistory();
        this.cancelRenderTasks();
      }
    },
    url() {
      if (this.value) {
        this.loadPdf();
      }
    },
  },
  mounted() {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.handleResize);
    }
  },
  beforeDestroy() {
    this.cleanupHistory();
    this.cancelRenderTasks();
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.handleResize);
    }
  },
  methods: {
    setupHistory() {
      if (typeof window !== "undefined" && !this.pushedHistory) {
        try {
          window.history.pushState({ pelastuskorttiModal: true }, "");
          this.pushedHistory = true;
          window.addEventListener("popstate", this.onPopState);
        } catch (e) {
          // ignore if history api unavailable
        }
      }
    },
    cleanupHistory() {
      if (typeof window !== "undefined") {
        window.removeEventListener("popstate", this.onPopState);
        this.pushedHistory = false;
      }
    },
    onPopState() {
      this.pushedHistory = false;
      this.dialog = false;
    },
    close() {
      if (
        this.pushedHistory &&
        typeof window !== "undefined" &&
        window.history.state &&
        window.history.state.pelastuskorttiModal
      ) {
        window.history.back();
      } else {
        this.dialog = false;
      }
    },
    cancelRenderTasks() {
      if (this.renderTasks && this.renderTasks.length) {
        this.renderTasks.forEach((task) => {
          try {
            task.cancel();
          } catch (e) {
            // ignore cancel errors
          }
        });
        this.renderTasks = [];
      }
    },
    async loadPdf() {
      if (!this.url) {
        this.loadError = "Pelastuskortin osoite puuttuu";
        this.loading = false;
        return;
      }

      this.loading = true;
      this.loadError = null;
      this.numPages = 0;
      this.zoom = 1.0;
      this.cancelRenderTasks();

      try {
        const [pdfjsLib, res] = await Promise.all([
          loadPdfJs(),
          fetch(this.url, { mode: "cors" }),
        ]);

        if (!res.ok) {
          throw new Error(`Tiedoston haku epäonnistui (HTTP ${res.status})`);
        }

        const arrayBuffer = await res.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({
          data: new Uint8Array(arrayBuffer),
        });

        this.pdfDoc = await loadingTask.promise;
        this.numPages = this.pdfDoc.numPages;
        this.loading = false;

        this.$nextTick(() => {
          this.renderPages();
        });
      } catch (err) {
        if (err.name === "RenderingCancelledException") return;
        console.error("PDF load error:", err);
        this.loadError = err.message || "Tuntematon virhe";
        this.loading = false;
      }
    },
    async renderPages() {
      if (!this.pdfDoc || this.numPages === 0) return;

      this.cancelRenderTasks();

      const container = this.$refs.canvasContainer;
      const containerWidth =
        (container && container.clientWidth) ||
        (typeof window !== "undefined" ? window.innerWidth : 600);

      for (let p = 1; p <= this.numPages; p++) {
        try {
          const page = await this.pdfDoc.getPage(p);
          const rawViewport = page.getViewport({ scale: 1.0 });

          // Fit page to container width minus margins
          const availableWidth = Math.max(containerWidth - 32, 280);
          const baseScale = availableWidth / rawViewport.width;
          const finalScale = Math.max(baseScale * this.zoom, 0.4);

          const viewport = page.getViewport({ scale: finalScale });

          const dpr = Math.max(
            (typeof window !== "undefined" && window.devicePixelRatio) || 1,
            2,
          );

          const canvasRef = this.$refs[`canvas_${p}`];
          const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
          if (!canvas) continue;

          const ctx = canvas.getContext("2d");
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;

          const transform = dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null;
          const renderTask = page.render({
            canvasContext: ctx,
            transform,
            viewport,
          });
          this.renderTasks.push(renderTask);
          await renderTask.promise;
        } catch (e) {
          if (e && e.name !== "RenderingCancelledException") {
            console.error(`Page ${p} render error:`, e);
          }
        }
      }
    },
    changeZoom(delta) {
      this.zoom = Math.max(0.5, Math.min(3.0, +(this.zoom + delta).toFixed(2)));
      this.renderPages();
    },
    resetZoom() {
      this.zoom = 1.0;
      this.renderPages();
    },
    handleResize() {
      if (this.dialog && !this.loading && this.numPages > 0) {
        this.renderPages();
      }
    },
  },
};
</script>
